import { Controller } from '@hotwired/stimulus'
import { scroll } from 'motion'

export default class extends Controller<HTMLElement> {
  static override targets = ['mark']

  declare readonly markTarget: SVGSVGElement

  private moving = false
  private following = false
  private frame = 0
  private cancels: Array<VoidFunction> = []

  override connect() {
    this.moving = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.following = this.moving && window.matchMedia('(hover: hover)').matches
    if (!this.moving) return

    const alive = new IntersectionObserver(([entry]) => {
      this.element.toggleAttribute('data-alive', Boolean(entry?.isIntersecting))
    })
    alive.observe(this.element)
    this.cancels.push(
      () => alive.disconnect(),
      scroll(
        (progress: number) => {
          this.markTarget.style.setProperty('--hero-exit', progress.toFixed(4))
        },
        { target: this.element, offset: ['start start', 'end start'] },
      ),
    )
  }

  override disconnect() {
    cancelAnimationFrame(this.frame)
    this.frame = 0
    for (const cancel of this.cancels.splice(0)) cancel()
  }

  poke() {
    if (!this.moving) return
    // Reflow between remove and re-add, so a second tap restarts the wiggle mid-flight.
    this.markTarget.removeAttribute('data-poke')
    void this.markTarget.getBoundingClientRect()
    this.markTarget.setAttribute('data-poke', '')
  }

  lean({ clientX }: PointerEvent) {
    if (!this.following || this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = 0
      const box = this.markTarget.getBoundingClientRect()
      const offset = (clientX - (box.left + box.width / 2)) / (box.width || 1)
      this.markTarget.style.setProperty(
        '--lean',
        `${(Math.max(-1, Math.min(1, offset)) * 2.2).toFixed(2)}deg`,
      )
    })
  }

  rest() {
    if (this.following) this.markTarget.style.setProperty('--lean', '0deg')
  }
}
